import type {
  DocumentBranding,
  EstimateDocLayout,
} from '@notifi/shared-types/estimate-doc-template';
import { layoutVisibility } from './layoutVisibility';

/**
 * The identity fields a single-scope document surface shows — a structural
 * superset of both `CompanyInfo` (public quote) and `PdfCompanyInfo` (portal), so
 * one resolver feeds every rail / letterhead / footer / consent surface.
 */
export interface SingleScopeCompany {
  name?: string | null;
  logoUrl?: string | null;
  logoDataUri?: string | null;
  tagline?: string | null;
  contractorLicense?: string | null;
  regulatoryNotice?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: {
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    country?: string | null;
  } | null;
  taxRegistrationNumber?: string | null;
}

const str = (v: string | null | undefined) => (v && v.trim() ? v.trim() : undefined);

/**
 * The identity a single-scope estimate/proposal actually shows the customer.
 *
 * A single-scope DESIGN can carry its own business identity (a Business Unit's
 * name/logo/contact/license, edited in the document builder). When the resolved
 * layout is that single-scope design, its branding wins field-by-field with the
 * tenant company as the per-field fallback (a blank design field inherits the
 * tenant's). Any other presentation returns the tenant company unchanged.
 *
 * ONE resolver so every identity/contact surface — rail, letterhead, footer, the
 * "issued by" note, the Ask link, and both authorize/payment consents — names the
 * same business. Mixing sources is exactly the split-identity bug this prevents.
 *
 * This is the WHOLE identity, ungated: the channels a customer may need to act
 * (the offline-approval note, the Ask link) must always be able to reach the
 * business, whatever the document prints — see `offlineApprovalNote`. What the
 * document PRINTS is `printableSingleScopeCompany` below.
 */
export function resolveSingleScopeCompany<T extends SingleScopeCompany>(
  company: T | null | undefined,
  resolvedDocLayout: EstimateDocLayout | null | undefined,
): T | SingleScopeCompany | null {
  const branding = resolvedDocLayout?.branding as
    | (DocumentBranding & { presentation?: string })
    | undefined;
  const ss = branding?.presentation === 'SINGLE_SCOPE' ? branding : null;
  if (!ss) return company ?? null;
  // A branding address counts as an override only when it actually carries a
  // line — a present-but-blank address object inherits the tenant's, exactly like
  // the blank string fields above (`str()` drops empty overrides). Without this a
  // `{ street: '', city: '', ... }` payload would suppress the tenant address.
  const brandingAddress =
    ss.address && Object.values(ss.address).some((v) => typeof v === 'string' && v.trim())
      ? ss.address
      : null;
  return {
    name: str(ss.displayName) ?? company?.name ?? 'Your provider',
    logoUrl: str(ss.logoUrl) ?? company?.logoUrl ?? null,
    logoDataUri: str(ss.logoDataUri) ?? str(company?.logoDataUri) ?? null,
    tagline: str(ss.tagline) ?? str(company?.tagline) ?? null,
    contractorLicense: str(ss.licenseNumber) ?? str(company?.contractorLicense) ?? null,
    regulatoryNotice: ss.regulatoryNotice ?? company?.regulatoryNotice ?? null,
    phone: str(ss.phone) ?? str(company?.phone) ?? null,
    email: str(ss.email) ?? str(company?.email) ?? null,
    address: brandingAddress ?? company?.address ?? null,
    taxRegistrationNumber:
      str(ss.taxRegistrationNumber) ?? str(company?.taxRegistrationNumber) ?? null,
  };
}

/**
 * The identity a single-scope document PRINTS — `resolveSingleScopeCompany`
 * with the contact fields the DESIGN hides removed. The design owns which of
 * `companyInfo` phone / email / address print (the builder's "Your business"
 * toggles), so the hosted rail + footer, the portal letterhead and the builder
 * preview all drop them exactly as the PDF does. Name, logo, tagline and
 * license are identity, never gated.
 *
 * Only for surfaces that PRINT the letterhead: the offline-approval note and
 * the Ask link keep the ungated identity so a customer can always reach the
 * business to answer the estimate.
 */
export function printableSingleScopeCompany<T extends SingleScopeCompany>(
  company: T | null | undefined,
  resolvedDocLayout: EstimateDocLayout | null | undefined,
): T | SingleScopeCompany | null {
  const resolved = resolveSingleScopeCompany(company, resolvedDocLayout);
  const presentation = (resolvedDocLayout?.branding as { presentation?: string } | undefined)
    ?.presentation;
  if (!resolved || presentation !== 'SINGLE_SCOPE') return resolved;
  const v = layoutVisibility(resolvedDocLayout);
  return {
    ...resolved,
    phone: v.companyPhone ? resolved.phone : null,
    email: v.companyEmail ? resolved.email : null,
    address: v.companyAddress ? resolved.address : null,
  };
}
